<template>
    <div class="health-card">
        <button type="button" class="health-card__btn" @click="open">
            <v-icon small>mdi-card-account-details-outline</v-icon>Κάρτα υγείας αθλητή
        </button>

        <v-dialog v-model="dialog" max-width="560">
            <div class="health-card__dialog">
                <div class="health-card__head">
                    <span>Κάρτα υγείας αθλητή</span>
                    <v-btn icon small dark @click="dialog = false">
                        <v-icon>mdi-close</v-icon>
                    </v-btn>
                </div>
                <div class="health-card__preview">
                    <v-progress-circular v-if="busy" indeterminate :color="accent" />
                    <div v-else-if="error" class="health-card__error">{{ error }}</div>
                    <div v-else-if="imageUrl" class="health-card__page">
                        <img :src="imageUrl" alt="">
                        <!-- the images the card could not draw (photo, team logo), laid over their place -->
                        <img v-for="(item, index) in overlays" :key="index" :src="item.src" alt="" class="health-card__overlay" :style="overlayStyle(item)">
                    </div>
                </div>
                <div v-if="imageUrl" class="health-card__actions">
                    <button type="button" class="health-card__main" @click="print">
                        <v-icon small>mdi-printer-outline</v-icon>{{ native ? 'Εκτύπωση / PDF' : 'Εκτύπωση' }}
                    </button>
                    <button v-if="!overlays.length" type="button" class="health-card__secondary" @click="download">
                        <v-icon small>mdi-download</v-icon>Λήψη
                    </button>
                </div>
                <p v-if="imageUrl && overlays.length && !native" class="health-card__hint">
                    Η φωτογραφία και το λογότυπο μπαίνουν στην εκτύπωση. Για αρχείο, διάλεξε «Αποθήκευση ως PDF» στο παράθυρο της εκτύπωσης.
                </p>
                <p v-if="imageUrl && native" class="health-card__hint">
                    Η «Εκτύπωση / PDF» ανοίγει την κάρτα σε A4: από εκεί εκτύπωση ή αποθήκευση στα αρχεία.
                </p>
                <p v-if="imageUrl" class="health-card__hint">
                    Τύπωσέ την σε Α4 και πήγαινέ την στον γιατρό για υπογραφή και σφραγίδα. Τα κενά πεδία συμπληρώνονται με το χέρι.
                </p>
            </div>
        </v-dialog>
    </div>
</template>

<script>
    import { renderHealthCard, cardPdf, toDataUrl } from '@/utils/healthCard'
    import { storageUrl } from '@/utils/matchImage'
    import { saveFile, isNativeApp } from '@/utils/saveFile'

    const LEAGUE = 'Commercial League'
    const ACCENT = '#037ade'

    // The athlete's health card of the player, filled in with what the site knows, to print
    export default {
        props: {
            player: {
                type: Object,
                required: true
            }
        },
        data() {
            return {
                dialog: false,
                busy: false,
                error: null,
                imageUrl: null,
                // the images that go over the card, as they could not be drawn in it
                overlays: [],
                accent: ACCENT
            }
        },
        computed: {
            fields() {
                const player = this.player
                const team = !player.freeAgent && player.team ? player.team.name : ''
                return [
                    { label: 'ΑΡ. ΜΗΤΡΩΟΥ', value: player.athleteNumber ? String(player.athleteNumber) : '', side: true },
                    { label: 'ΟΜΑΔΑ', value: team || '', side: true, team: true },
                    { label: 'ΑΡ. ΦΑΝΕΛΑΣ', value: this.jersey, side: true },
                    { label: 'ΥΨΟΣ', value: this.height, side: true },
                    // the full name is kept in one field when there is no surname
                    ...(player.surname
                        ? [{ label: 'ΟΝΟΜΑ', value: String(player.name || '').trim() }, { label: 'ΕΠΩΝΥΜΟ', value: String(player.surname).trim() }]
                        : [{ label: 'ΟΝΟΜΑΤΕΠΩΝΥΜΟ', value: String(player.name || '').trim() }]),
                    { label: 'ΠΑΤΡΩΝΥΜΟ', value: '' },
                    // only the year is known
                    { label: 'ΗΜ. ΓΕΝΝΗΣΗΣ', value: player.birthdayYear ? `....... / ....... / ${player.birthdayYear}` : (player.birthday || '') },
                    { label: 'Α.Μ.Κ.Α.', value: '' }
                ]
            },
            photoUrl() {
                return this.player.image ? storageUrl(this.player.image) : null
            },
            teamLogoUrl() {
                const team = !this.player.freeAgent && this.player.team
                return team && team.logo ? storageUrl(team.logo) : null
            },
            // the number on the shirt: the alternative one when the player has one
            jersey() {
                const value = [this.player.alternativeJersey, this.player.jersey].find(item => item !== null && item !== undefined && item !== '')
                return value === undefined ? '' : String(value)
            },
            // stored in metres or in centimetres
            height() {
                const value = Number(this.player.height)
                if (!value) return ''
                return `${Math.round(value < 3 ? value * 100 : value)} cm`
            },
            // the page of the player: what the QR holds, read by the stats app of the CMS
            playerLink() {
                return new URL(this.$router.resolve({ name: 'Player', params: { id: this.player.id } }).href, window.location.href).href
            },
            native() {
                return isNativeApp()
            },
            fileName() {
                const name = [this.player.name, this.player.surname].filter(Boolean).join('-')
                return `karta-ygeias-${name}`.replace(/[\s/\\:*?"<>|.]+/g, '-')
            }
        },
        methods: {
            overlayStyle(item) {
                return { left: `${item.left}%`, top: `${item.top}%`, width: `${item.width}%`, height: `${item.height}%`, objectFit: item.fit }
            },
            async open() {
                this.dialog = true
                if (this.imageUrl || this.busy) return
                this.busy = true
                this.error = null
                try {
                    const { blob, overlays } = await renderHealthCard({
                        league: LEAGUE,
                        logo: require('@/assets/main_logo.png'),
                        accent: ACCENT,
                        photo: this.photoUrl,
                        teamLogo: this.teamLogoUrl,
                        fields: this.fields,
                        site: window.location.host,
                        qr: this.playerLink
                    })
                    this.overlays = overlays
                    this.imageUrl = await toDataUrl(blob)
                } catch (error) {
                    this.error = 'Η κάρτα δεν μπόρεσε να δημιουργηθεί.'
                } finally {
                    this.busy = false
                }
            },
            download() {
                saveFile(this.imageUrl, `${this.fileName}.png`, 'Κάρτα υγείας αθλητή').catch(() => {
                    this.error = 'Η κάρτα δεν μπόρεσε να αποθηκευτεί.'
                })
            },
            // the card alone on an A4 page, from a hidden frame; in the app, where the page cannot print, as a pdf
            // for the share sheet of the phone (print, files)
            print() {
                if (this.native) {
                    return cardPdf(this.imageUrl)
                        .then(pdf => saveFile(pdf, `${this.fileName}.pdf`, 'Κάρτα υγείας αθλητή'))
                        .catch(() => {
                            this.error = 'Η κάρτα δεν μπόρεσε να αποθηκευτεί.'
                        })
                }
                const frame = document.createElement('iframe')
                frame.style.cssText = 'position:fixed;width:0;height:0;border:0;right:0;bottom:0'
                document.body.appendChild(frame)
                const doc = frame.contentWindow.document
                doc.open()
                // the images the card could not draw, laid over their place
                const photo = this.overlays
                    .map(item => `<img src="${item.src}" style="position:absolute;left:${item.left}%;top:${item.top}%;width:${item.width}%;height:${item.height}%;object-fit:${item.fit}">`)
                    .join('')
                doc.write(`<!doctype html><html><head><title>${this.fileName}</title><style>@page{size:A4;margin:0}html,body{margin:0}.page{position:relative}.page>img:first-child{display:block;width:100%;height:auto}</style></head><body><div class="page"><img src="${this.imageUrl}">${photo}</div></body></html>`)
                doc.close()
                // once every image of the page is in (a photo that fails is left out)
                const images = [...doc.querySelectorAll('img')]
                Promise.all(images.map(image => (image.complete ? null : new Promise(resolve => { image.onload = resolve; image.onerror = resolve }))))
                    .then(() => {
                        frame.contentWindow.focus()
                        frame.contentWindow.print()
                        setTimeout(() => frame.remove(), 1000)
                    })
            }
        }
    }
</script>

<style scoped>
.health-card {
    /* the chips above keep their own space; this one keeps it from the figures below */
    margin-bottom: 16px;
}

.health-card__btn {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 8px 14px;
    border: 1px solid rgba(3, 229, 183, 0.6);
    border-radius: 999px;
    background: rgba(255, 255, 255, 0.08);
    color: #fff;
    font-family: Roboto, Arial, sans-serif;
    font-size: 0.85rem;
    font-weight: 700;
    cursor: pointer;
    transition: background 0.15s ease;
}

.health-card__btn .v-icon {
    color: #03e5b7 !important;
}

.health-card__btn:hover {
    background: linear-gradient(315deg, #03e5b7 0%, #037ade 74%);
    color: #1b1b1b;
}

.health-card__btn:hover .v-icon {
    color: #1b1b1b !important;
}

.health-card__dialog {
    padding: 16px;
    border-radius: 16px;
    background: #232323;
    color: #fff;
    font-family: Roboto, Arial, sans-serif;
}

.health-card__head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 12px;
    font-size: 1.1rem;
    font-weight: 800;
}

.health-card__preview {
    display: flex;
    align-items: center;
    justify-content: center;
    aspect-ratio: 1240 / 1754;
    border-radius: 8px;
    background: #fff;
    overflow: hidden;
}

.health-card__page {
    position: relative;
    width: 100%;
}

.health-card__page img {
    display: block;
    width: 100%;
    height: auto;
}

.health-card__page .health-card__overlay {
    position: absolute;
}

.health-card__error {
    padding: 24px;
    color: #555;
    text-align: center;
}

.health-card__actions {
    display: flex;
    gap: 10px;
    margin-top: 14px;
}

.health-card__main,
.health-card__secondary {
    display: inline-flex;
    flex: 1;
    align-items: center;
    justify-content: center;
    gap: 6px;
    padding: 10px 14px;
    border-radius: 10px;
    color: #fff;
    font-weight: 700;
    cursor: pointer;
}

.health-card__main {
    background: linear-gradient(315deg, #03e5b7 0%, #037ade 74%);
    color: #1b1b1b;
}

.health-card__main .v-icon {
    color: #1b1b1b !important;
}

.health-card__secondary {
    border: 1px solid rgba(255, 255, 255, 0.25);
}

.health-card__main .v-icon,
.health-card__secondary .v-icon {
    color: #fff !important;
}

.health-card__hint {
    margin: 10px 0 0;
    color: rgba(255, 255, 255, 0.55);
    font-size: 0.8rem;
}
</style>
